import {mutationOptions, useMutation} from '@tanstack/react-query'
import * as sdk from '@cypro/server/sdk'
import {Tolgee} from '@cypro/ui/integrations/tolgee'
import {localTimeZone} from '@cypro/ui/constants'

function extractFilename(response: Response) {
  const contentDisposition = response.headers.get('Content-Disposition')
  if (contentDisposition) {
    const match = contentDisposition.match(/filename="(.+?)"/)
    if (match?.[1]) return match[1]
  }

  return ''
}

function triggerDownload(blob: Blob, response: Response, fallbackFilename: string) {
  const filename = extractFilename(response) || fallbackFilename

  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()

  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

export function exportScaleAlibisCsvReport() {
  return mutationOptions({
    mutationFn: async (therapySequenceId: string) => {
      const {response, data, error} = await sdk.getScaleAlibisCsv({
        query: {therapySequenceId, locale: Tolgee.locale, timezone: localTimeZone},
      })

      if (error) throw error
      if (typeof data !== 'string') throw new Error('Invalid CSV response format.')
      if (!response) throw new Error('No response')

      const blob = new Blob([data], {type: 'text/csv'})

      return triggerDownload(blob, response, `scale-alibis-${therapySequenceId}.csv`)
    },
  })
}

export function exportScaleAlibisExcelReport() {
  return mutationOptions({
    mutationFn: async (therapySequenceId: string) => {
      const {response, data, error} = await sdk.getScaleAlibisExcel({
        query: {therapySequenceId, locale: Tolgee.locale, timezone: localTimeZone},
      })

      if (error) throw error
      if (!(data instanceof Blob)) throw new Error('Invalid Excel response format.')
      if (!response) throw new Error('No response')

      return triggerDownload(data, response, `scale-alibis-${therapySequenceId}.xlsx`)
    },
  })
}

export function exportScaleAlibisPdfReport() {
  return mutationOptions({
    mutationFn: async (therapySequenceId: string) => {
      const {response, data, error} = await sdk.getScaleAlibisPdf({
        query: {therapySequenceId, locale: Tolgee.locale, timezone: localTimeZone},
      })

      if (error) throw error
      if (!(data instanceof Blob)) throw new Error('Invalid PDF response format.')
      if (!response) throw new Error('No response')

      return triggerDownload(data, response, `scale-alibis-${therapySequenceId}.pdf`)
    },
  })
}

export function useExportScaleAlibisCsvReport() {
  return useMutation(exportScaleAlibisCsvReport())
}

export function useExportScaleAlibisExcelReport() {
  return useMutation(exportScaleAlibisExcelReport())
}

export function useExportScaleAlibisPdfReport() {
  return useMutation(exportScaleAlibisPdfReport())
}
